import { useRef, useState } from 'react';
import ReactToPrint from 'react-to-print';

import { useQueryClient } from '@tanstack/react-query';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { usePatient } from 'lib/hooks/queries/patients/usePatient';

import PrintView from 'components/Patient/quote/components/PrintView';
import Configurations from 'components/Payments/Configurations';
import InvoiceReadOnly from 'components/Payments/components/InvoiceReadOnly';
import { currentPractice } from 'components/practice/practiceState';
import Button from 'components/shared/Buttons/Button';
import Modal from 'components/shared/Modal/Modal';
import Popover from 'components/shared/Popovers/Popover/Popover';

export const InvoiceActionPopover = ({ data }) => {
  const invoice = data || {};

  const ref = useRef();

  const practice = useRecoilValue(currentPractice);
  const queryClient = useQueryClient();

  const patientData = usePatient({
    params: { id: invoice?.patient_id },
    dependencies: [invoice?.patient_id]
  });

  const patient = patientData?.data?.patient || {};

  const [mode, setMode] = useState({ preview: false, pay: false });
  const [show, setShow] = useState(false);

  const actionsOptions = [
    {
      label: 'Preview Details',
      icon: 'eye-mono',
      onClick: () => handleOpen({ mode: 'preview' })
    },
    {
      label: 'Pay Balance',
      icon: 'wallet-add-mono',
      onClick: () => handleOpen({ mode: 'pay' })
    }
  ];

  const handleOpen = ({ mode }) => {
    setShow(true);
    setMode((prev) => ({ ...prev, [mode]: true }));
  };

  const handleClose = () => {
    setShow(false);
    setMode({ preview: false, pay: false });
    queryClient.invalidateQueries({ queryKey: ['patientInvoices'], refetchType: 'active' });
    queryClient.refetchQueries({ queryKey: ['patientBalance'] });
  };

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={actionsOptions}
        isBorder={false}
      />
      <Modal
        slideFromRight
        isOpen={show}
        isLarge
        bodyClassName="!p-0"
        title="Invoice"
        className={cs('bg-white', mode?.preview && 'w-[800px]')}
        headButton={
          <div className="flex w-full justify-end">
            <ReactToPrint
              trigger={() => (
                <Button
                  color=""
                  size="small"
                  type="link"
                  text="Print"
                  icon="printer-mono"
                  className="text-primary-900"
                />
              )}
              content={() => ref?.current}
            />
          </div>
        }
        handleClose={handleClose}>
        {mode?.preview && (
          <div ref={ref}>
            <PrintView
              patient={patient}
              practice={practice}
              createdAt={invoice?.dateofservice}
              content={<InvoiceReadOnly invoices={[invoice]} />}
            />
          </div>
        )}

        {mode?.pay && (
          <Configurations
            newInvoice={[
              {
                ...invoice,
                balance: invoice.balance,
                expanded: true,
                selected: true
              }
            ]}
            amount={invoice.balance}
            patientId={patient?.id}
            onClose={handleClose}
          />
        )}
      </Modal>
    </>
  );
};

export default InvoiceActionPopover;
